import React, { useEffect, useState } from 'react';
import AOS from 'aos';
import 'aos/dist/aos.css';


/* ─────────────────────────────────────────────────────────────
   DATA — sourced strictly from product PDFs only
───────────────────────────────────────────────────────────── */
const categories = [
  
  {
    id: 'vms',
    label: 'Sentry VMS',
    icon: 'fa fa-video-camera',
    accent: '#15803d',
    accentLight: '#dcfce7',
    accentGlow: 'rgba(21,128,61,0.18)',
    description:
      'Sentry VMS is an enterprise-class, AI-powered surveillance platform supporting minimum 5,000 cameras from day one with unlimited cameras via modular licensing. ONVIF Profiles S, G, T, M conformant — no vendor lock-in.',
    features: [
      {
        icon: 'fa fa-desktop',
        title: 'Live Monitoring & Display',
        desc: '64+ cameras simultaneously with low-latency streaming and hardware-accelerated decoding (NVIDIA, Intel QuickSync). Configurable matrix 1x1 to 8x8, video wall with 16+ screen stitching, and adaptive streaming based on network conditions.',
      },
      {
        icon: 'fa fa-search',
        title: 'Smart Search & Forensics',
        desc: 'Attribute-based search (clothing color, gender, accessories), motion-based smart search with region selection, video summarization to compress hours into minutes, and cross-camera tracking with person/vehicle correlation.',
      },
      {
        icon: 'fa fa-hdd-o',
        title: 'Recording & Storage',
        desc: 'Continuous, motion-activated, event-triggered, and schedule-based recording. Pre-buffer up to 30 seconds for complete event capture. Supports local (DAS/RAID), NAS/SAN, and cloud storage (S3-compatible, Azure Blob, Google Cloud).',
      },
      {
        icon: 'fa fa-film',
        title: 'Advanced Playback',
        desc: '16-channel synchronized playback, variable speed 0.125x to 32x, frame-by-frame forensic analysis, evidence locking to prevent tampering or deletion, bookmarking with notes, and export in MP4/AVI/MJPEG with optional encryption.',
      },
      {
        icon: 'fa fa-arrows-alt',
        title: 'PTZ Control',
        desc: '8-directional control with variable speed, optical zoom with focus and iris adjustment, 256+ preset positions per camera, automated patrol tours with dwell times, and pattern recording and playback.',
      },
      {
        icon: 'fa fa-map',
        title: 'Interactive E-Maps & GIS',
        desc: 'Unlimited hierarchical maps and sub-maps with Google Maps, OpenStreetMap, and Bing Maps integration. Static floor plans with camera overlay, real-time event markers, and live video playback directly on map.',
      },
      {
        icon: 'fa fa-lock',
        title: 'Multi-Layer Cybersecurity',
        desc: 'TLS 1.3 for all communications, AES-256 video encryption, multi-factor authentication (MFA), LDAP/Active Directory integration, role-based permissions (RBAC), OWASP compliance, VAPT certified, and GDPR compliant.',
      },
      {
        icon: 'fa fa-refresh',
        title: 'High Availability & Failover',
        desc: 'N:1, N:N, and N:0 failover configurations, load balancing across recording servers, automatic failover with seamless transition, and database replication and backup with master-master data synchronization.',
      },
      {
        icon: 'fa fa-mobile',
        title: 'Mobile Access',
        desc: 'Native iOS and Android apps with multi-site monitoring, live view, PTZ control with gestures, event notifications and alerts, recorded video playback, snapshot capture, camera health monitoring, and biometric authentication.',
      },
    ],
  },
  {
    id: 'ai-analytics',
    label: 'Sentry AI Pro',
    icon: 'fa fa-eye',
    accent: '#1d4ed8',
    accentLight: '#dbeafe',
    accentGlow: 'rgba(29,78,216,0.18)',
    description:
      'SENTRY AI PRO converts live RTSP camera streams into actionable insights without requiring heavy data storage or complex infrastructure. Delivers AI-powered monitoring across 9 comprehensive capabilities — all managed from a centralized dashboard.',
    features: [
      {
        icon: 'fa fa-video-camera',
        title: 'Camera Tampering Detection',
        desc: 'Ensures surveillance integrity by detecting camera blocking, lens covering, sudden repositioning or angle changes, and focus/lighting interference. Instant alerts ensure uninterrupted security monitoring.',
      },
      {
        icon: 'fa fa-users',
        title: 'People Count & Analytics',
        desc: 'Tracks total visitors, peak hours, repeat visitor frequency (excluding employees), abnormal crowd detection with configurable thresholds, and after-hours people count for compliance audits.',
      },
      {
        icon: 'fa fa-map-marker',
        title: 'Restricted Line Crossing',
        desc: 'Virtual boundary creation with detection of line crossings, directional logic (entry/exit-only), and time-based configuration. Ideal for secure counters, document areas, and service zones.',
      },
      {
        icon: 'fa fa-user-secret',
        title: 'Person Tracking in Restricted Areas',
        desc: 'Tracks individuals entering record rooms and secure areas. Logs visit timestamps, dwell time, maintains structured visit records, and enables investigation-based playback for traceability.',
      },
      {
        icon: 'fa fa-moon-o',
        title: 'Motion Detection After Hours',
        desc: 'Automated surveillance beyond working hours with time-based activation, intelligent filtering to reduce false alerts, and immediate notifications via SMS, Email, or App for unauthorized access prevention.',
      },
      {
        icon: 'fa fa-fire',
        title: 'Fire & Smoke Detection',
        desc: 'Real-time detection of fire and smoke through AI-powered visual analysis. Provides early warning alerts to enable rapid emergency response, minimizing potential damage and ensuring occupant safety across facilities.',
      },
      {
        icon: 'fa fa-ban',
        title: 'No Parking Zone Enforcement',
        desc: 'Automatically detects vehicles parked in restricted or no-parking zones. Configurable time-based rules and instant violation alerts enable enforcement teams to respond quickly and maintain traffic flow and compliance.',
      },
      {
        icon: 'fa fa-car',
        title: 'Intelligent Parking Management',
        desc: 'Monitors allowed vehicle types (authorized vehicles only), tracks individual vehicle parking duration, detects overstay violations, and generates occupancy analytics for optimized parking space management and compliance.',
      },
      {
        icon: 'fa fa-crosshairs',
        title: 'Weapon & Gun Detection',
        desc: 'AI-powered identification of firearms and weapons in camera view. Critical for high-security environments, providing immediate threat alerts to security personnel for rapid response and incident prevention.',
      },
    ],
  },
  {
    id: 'storage',
    label: 'DropVault',
    icon: 'fa fa-lock',
    accent: '#a16207',
    accentLight: '#fef9c3',
    accentGlow: 'rgba(161,98,7,0.18)',
    description:
      'DropVault is an Intelligent Storage Management Platform that transforms how organizations manage critical documents and video records across distributed environments through centralized intelligence and military-grade security.',
    features: [
      {
        icon: 'fa fa-tachometer',
        title: 'Centralized Intelligence & Control',
        desc: 'Single unified dashboard to manage and monitor all connected systems. Real-time visibility of all connected edge systems, centralised system health and performance monitoring, version tracking and lifecycle management, and remote deployment and updates of client applications.',
      },
      {
        icon: 'fa fa-sitemap',
        title: 'High Availability Architecture',
        desc: 'Redundant web and application layers, load-balanced services for consistent performance, distributed architecture for fault tolerance, automatic failover mechanisms, and scalable multi-node infrastructure.',
      },
      {
        icon: 'fa fa-database',
        title: 'Centralized Storage & Secure Collaboration',
        desc: 'Centralized repository for secure storage of data and video files from all remote locations. Role-based access with granular control. Multi-location access from any authorized facility. Optimized protocols for rapid upload and data retrieval.',
      },
      {
        icon: 'fa fa-share-alt',
        title: 'Secure External Sharing',
        desc: 'Controlled sharing with internal and external stakeholders through encrypted channels. Secure web-based access links, controlled file download access, configurable link expiration policies, external platform integrations, and encrypted access channels.',
      },
      {
        icon: 'fa fa-laptop',
        title: 'Smart Client Application',
        desc: 'Dedicated client for secure data capture and transfer from remote locations, even in unstable networks. Event-based data uploads, metadata uploads with date-time selection, local caching during disruptions, automatic synchronization, and background transfer mechanisms.',
      },
      {
        icon: 'fa fa-hdd-o',
        title: 'Scalable Object Storage Architecture',
        desc: 'Scale-out architecture with unified namespace (100s of PB scale), high-throughput performance tailored for large files, flexible hardware expansion within the same cluster, and optimized for durability and long-term performance.',
      },
      {
        icon: 'fa fa-recycle',
        title: 'Data Protection & Resilience',
        desc: 'Erasure coding-based protection against node failures, distributed data storage for fault tolerance, automatic data recovery and reconstruction, and continuous data integrity validation.',
      },
      {
        icon: 'fa fa-shield',
        title: 'Enterprise Security',
        desc: 'WORM (Write Once Read Many), ransomware detection and recovery, encrypted transfer and storage, Enterprise Identity (AD/LDAP), and Retention & Immutability control.',
      },
      {
        icon: 'fa fa-file-text',
        title: 'Audit & Compliance',
        desc: 'Non-repudiable audit trails, detailed access and upload logs, compliance-ready reporting, long-term audit data retention, and customizable insight reports.',
      },
    ],
  },
];

/* ─────────────────────────────────────────────────────────────
   SUB-COMPONENTS
───────────────────────────────────────────────────────────── */


const FeatureCard = ({ feature, accent, accentLight, delay }) => (
  <div
    className="sf-feat-card"
    data-aos="fade-up"
    data-aos-delay={delay}
    onMouseEnter={e => {
      e.currentTarget.style.transform = 'translateY(-5px)';
      e.currentTarget.style.boxShadow = `0 16px 40px ${accentLight}99`;
      e.currentTarget.style.borderColor = accent;
    }}
    onMouseLeave={e => {
      e.currentTarget.style.transform = 'translateY(0)';
      e.currentTarget.style.boxShadow = '0 2px 8px rgba(15,23,42,0.05)';
      e.currentTarget.style.borderColor = '#e2e8f0';
    }}
  >
    <div className="sf-feat-icon" style={{ background: accentLight, color: accent }}>
      <i className={feature.icon} />
    </div>
    <div className="sf-feat-content">
      <h4 className="sf-feat-title" style={{ color: '#0f172a' }}>{feature.title}</h4>
      <p className="sf-feat-desc">{feature.desc}</p>
    </div>
    {/* <div className="sf-feat-arrow" style={{ color: accent }}>
      <i className="fa fa-arrow-right" />
    </div> */}
  </div>
);

const CategoryPanel = ({ cat, isActive }) => (
  <div className={`sf-panel ${isActive ? 'sf-panel--active' : ''}`}>
    {/* Panel intro */}
    <div className="sf-panel-header" data-aos="fade-right">
      <div className="sf-panel-badge" style={{ background: cat.accentLight, color: cat.accent }}>
        <i className={cat.icon} /> {cat.label}
      </div>
      <p className="sf-panel-desc">{cat.description}</p>
    </div>

    {/* Feature grid */}
    <div className="sf-feat-grid">
      {cat.features.map((f, i) => (
        <FeatureCard
          key={i}
          feature={f}
          accent={cat.accent}
          accentLight={cat.accentLight}
          delay={i * 80}
        />
      ))}
    </div>
  </div>
);

/* ─────────────────────────────────────────────────────────────
   MAIN EXPORT
───────────────────────────────────────────────────────────── */
export const SentryFunctionalities = () => {
  const [active, setActive] = useState(0);

  useEffect(() => {
    AOS.init({ duration: 700, once: true });

    // Listen for tab-select events dispatched by SentryProducts cards
    const handler = (e) => {
      const idx = categories.findIndex(c => c.id === e.detail.tabId);
      if (idx !== -1) setActive(idx);
    };
    window.addEventListener('sentry-select-tab', handler);
    return () => window.removeEventListener('sentry-select-tab', handler);
  }, []);

  const cat = categories[active];

  return (
    <section className="sf-section" id="sentry-functionalities">
      <style>{`
        /* ── Google Font ── */
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@700;800&family=DM+Sans:ital,wght@0,400;0,500;0,600;1,400&display=swap');

        /* ─────────── Section ─────────── */
        .sf-section {
        //   font-family: 'DM Sans', sans-serif;
          background: linear-gradient(160deg, #f8fafc 0%, #f1f5f9 50%, #f0f9ff 100%);
          padding: 96px 0 80px;
          position: relative;
          overflow: hidden;
        }

        /* Decorative grid lines */
        .sf-section::before {
          content: '';
          position: absolute;
          inset: 0;
          background-image:
            linear-gradient(rgba(99,102,241,0.05) 1px, transparent 1px),
            linear-gradient(90deg, rgba(99,102,241,0.05) 1px, transparent 1px);
          background-size: 48px 48px;
          pointer-events: none;
        }

        /* Ambient glow blobs */
        .sf-glow {
          position: absolute;
          border-radius: 50%;
          filter: blur(90px);
          pointer-events: none;
          transition: background 0.6s ease;
        }
        .sf-glow--tl {
          width: 500px; height: 500px;
          top: -180px; left: -160px;
          opacity: 0.5;
        }
        .sf-glow--br {
          width: 400px; height: 400px;
          bottom: -120px; right: -100px;
          opacity: 0.4;
        }

        /* ─────────── Header ─────────── */
        .sf-header {
          text-align: center;
          margin-bottom: 64px;
          position: relative;
          z-index: 2;
        }
        .sf-eyebrow {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          font-size: 10px;
          font-weight: 700;
          letter-spacing: 3.5px;
          text-transform: uppercase;
          color: #6366f1;
          background: rgba(99,102,241,0.08);
          border: 1px solid rgba(99,102,241,0.2);
          padding: 5px 16px;
          border-radius: 24px;
          margin-bottom: 20px;
        }
        .sf-eyebrow i { font-size: 9px; }
        .sf-header h2 {
        //   font-family: 'Syne', sans-serif;
          color: #0f172a;
          font-size: 42px;
          font-weight: 800;
          margin: 0 0 16px;
          line-height: 1.15;
          letter-spacing: -0.5px;
        }
        .sf-header h2 span {
          background: linear-gradient(90deg, #6366f1, #0ea5e9);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        .sf-header p {
          color: #64748b;
          font-size: 16px;
          max-width: 560px;
          margin: 0 auto;
          line-height: 1.8;
        }

        /* ─────────── Tab bar ─────────── */
        .sf-tabs {
          display: flex;
          justify-content: center;
          gap: 10px;
          margin-bottom: 56px;
          position: relative;
          z-index: 2;
          flex-wrap: wrap;
        }
        .sf-tab {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 10px 22px;
          border-radius: 10px;
          border: 1px solid #e2e8f0;
          background: #ffffff;
          color: #64748b;
        //   font-family: 'DM Sans', sans-serif;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.25s ease;
          letter-spacing: 0.2px;
          box-shadow: 0 1px 4px rgba(15,23,42,0.06);
        }
        .sf-tab i { font-size: 12px; }
        .sf-tab:hover {
          background: #f8fafc;
          color: #0f172a;
          border-color: #cbd5e1;
        }
        .sf-tab--active {
          color: #ffffff !important;
          border-color: transparent !important;
        }

        /* ─────────── Panel ─────────── */
        .sf-panel {
          display: none;
          position: relative;
          z-index: 2;
        }
        .sf-panel--active { display: block; }

        /* ─────────── Panel header ─────────── */
        .sf-panel-header {
          margin-bottom: 40px;
        }
        .sf-panel-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 2px;
          text-transform: uppercase;
          padding: 5px 14px;
          border-radius: 20px;
          margin-bottom: 14px;
        }
        .sf-panel-desc {
          color: #64748b;
          font-size: 15px;
          line-height: 1.8;
          max-width: 680px;
          margin: 0 0 28px;
        }

        /* ─────────── Feature grid ─────────── */
        .sf-feat-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 16px;
        }

        /* ─────────── Feature card ─────────── */
        .sf-feat-card {
          display: flex;
          align-items: flex-start;
          gap: 14px;
          background: #ffffff;
          border: 1px solid #e2e8f0;
          border-radius: 16px;
          padding: 22px 20px;
          cursor: pointer;
          transition: transform 0.28s ease, box-shadow 0.28s ease, border-color 0.28s ease;
          position: relative;
          overflow: hidden;
          box-shadow: 0 2px 8px rgba(15,23,42,0.05);
        }
        .sf-feat-card::before {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(135deg, rgba(255,255,255,0.8) 0%, transparent 60%);
          pointer-events: none;
        }
        .sf-feat-icon {
          width: 42px;
          height: 42px;
          border-radius: 11px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          font-size: 15px;
        }
        .sf-feat-content { flex: 1; }
        .sf-feat-title {
        //   font-family: 'Syne', sans-serif;
          font-size: 14px;
          font-weight: 700;
          margin: 0 0 6px;
          color: #0f172a !important;
          letter-spacing: -0.1px;
        }
        .sf-feat-desc {
          font-size: 12.5px;
          color: #64748b;
          line-height: 1.65;
          margin: 0;
        }
        .sf-feat-arrow {
          font-size: 11px;
          margin-top: 4px;
          opacity: 0;
          transition: opacity 0.2s ease, transform 0.2s ease;
          flex-shrink: 0;
        }
        .sf-feat-card:hover .sf-feat-arrow {
          opacity: 1;
          transform: translateX(3px);
        }

        /* ─────────── Bottom CTA strip ─────────── */
        .sf-cta-strip {
          margin-top: 60px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 24px;
          background: #ffffff;
          border: 1px solid #e2e8f0;
          border-radius: 20px;
          padding: 28px 36px;
          position: relative;
          z-index: 2;
          flex-wrap: wrap;
          box-shadow: 0 4px 20px rgba(15,23,42,0.07);
        }
        .sf-cta-left h3 {
        //   font-family: 'Syne', sans-serif;
          color: #0f172a;
          font-size: 20px;
          font-weight: 800;
          margin: 0 0 6px;
        }
        .sf-cta-left p {
          color: #64748b;
          font-size: 13px;
          margin: 0;
        }
        .sf-cta-btns {
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
        }
        .sf-cta-btn {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 11px 22px;
          border-radius: 9px;
        //   font-family: 'DM Sans', sans-serif;
          font-size: 13px;
          font-weight: 700;
          text-decoration: none !important;
          cursor: pointer;
          transition: filter 0.22s ease, transform 0.22s ease;
          border: none;
        }
        .sf-cta-btn:hover {
          filter: brightness(1.1);
          transform: translateY(-2px);
        }
        .sf-cta-btn--primary {
          background: linear-gradient(135deg, #6366f1, #4f46e5);
          color: #fff !important;
          box-shadow: 0 6px 20px rgba(99,102,241,0.35);
        }
        .sf-cta-btn--ghost {
          background: transparent;
          color: #64748b !important;
          border: 1px solid #e2e8f0;
        }
        .sf-cta-btn--ghost:hover { color: #0f172a !important; border-color: #cbd5e1; }

        /* ─────────── RESPONSIVE ─────────── */
        @media (max-width: 1024px) {
          .sf-feat-grid { grid-template-columns: repeat(2, 1fr); }
          .sf-header h2 { font-size: 34px; }
        }

        @media (max-width: 768px) {
          .sf-section { padding: 64px 0 56px; }
          .sf-header h2 { font-size: 26px; }
          .sf-header p  { font-size: 14px; }
          .sf-feat-grid { grid-template-columns: 1fr; }
          .sf-cta-strip { flex-direction: column; align-items: flex-start; padding: 24px; }
          .sf-tabs { gap: 8px; }
          .sf-tab { font-size: 12px; padding: 9px 16px; }
        }

        @media (max-width: 480px) {
          .sf-header h2 { font-size: 22px; }
          .sf-cta-btns { width: 100%; }
          .sf-cta-btn { justify-content: center; flex: 1; }
          .sf-feat-card { padding: 18px 16px; }
        }
      `}</style>

      {/* Ambient glows that shift with active tab */}
      <div className="sf-glow sf-glow--tl" style={{ background: cat.accentGlow }} />
      <div className="sf-glow sf-glow--br" style={{ background: cat.accentGlow }} />

      <div className="container" style={{ position: 'relative', zIndex: 2 }}>

        {/* ── Section header ── */}
        <div className="sf-header" data-aos="fade-up">
          <div className="sf-eyebrow">
            <i className="fa fa-th-large" /> Full Platform Capabilities
          </div>
          <h2>
            Everything Your Security&nbsp;
            <span>Infrastructure Needs</span>
          </h2>
          <p>
            From AI surveillance capabilities to enterprise video management and
            military-grade storage — every feature is sourced directly from our product documentation.
          </p>
        </div>

        {/* ── Tab bar ── */}
        <div className="sf-tabs" data-aos="fade-up" data-aos-delay="100">
          {categories.map((c, i) => (
            <button
              key={c.id}
              className={`sf-tab ${active === i ? 'sf-tab--active' : ''}`}
              onClick={() => setActive(i)}
              style={
                active === i
                  ? { background: c.accent, boxShadow: `0 6px 20px ${c.accentGlow}` }
                  : {}
              }
            >
              <i className={c.icon} /> {c.label}
            </button>
          ))}
        </div>

        {/* ── Active panel ── */}
        {categories.map((c, i) => (
          <CategoryPanel key={c.id} cat={c} isActive={active === i} />
        ))}

        {/* ── CTA strip ── */}
        {/* <div className="sf-cta-strip" data-aos="fade-up">
          <div className="sf-cta-left">
            <h3>Ready to see it live?</h3>
            <p>Schedule a personalised demo or download the full platform datasheet.</p>
          </div>
          <div className="sf-cta-btns">
            <a href="#contact" className="sf-cta-btn sf-cta-btn--primary">
              <i className="fa fa-calendar" /> Book a Demo
            </a>
            <a href="#" className="sf-cta-btn sf-cta-btn--ghost">
              <i className="fa fa-file-pdf-o" /> Platform Datasheet
            </a>
          </div>
        </div> */}

      </div>
    </section>
  );
};

export default SentryFunctionalities;